import { Alert } from "@merge-api/merge-javascript-shared";
import { useEffect, useState } from "react";
import useBlueprintContext from "../../context/useBlueprintContext";
import { getStepForStepID } from "../../utils/BlueprintEditorUtils";
import { BlueprintStep } from "../../../../models/Blueprints";

interface Props {
  canvasRef: React.RefObject<HTMLDivElement>;
  isTracingBannerVisible: boolean;
}

const StaleParametersBanner = ({ canvasRef, isTracingBannerVisible }: Props) => {
  const { blueprint, frontendStaleParameters, setSelectedStep } = useBlueprintContext();

  const [style, setStyle] = useState({ width: "", left: "" });
  const [dismissed, setDismissed] = useState(false);

  const calculateOffset = () => {
    if (canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      setStyle({
        width: `${rect.width - 40}px`,
        left: `${rect.left}px`,
      });
    }
  };

  useEffect(() => {
    calculateOffset();
    window.addEventListener("resize", calculateOffset);
    return () => window.removeEventListener("resize", calculateOffset);
  }, []);

  if (frontendStaleParameters.size === 0 || dismissed) return null;

  // Offset downward if the tracing banner is also visible so they don't overlap
  const topClass = isTracingBannerVisible ? "mt-30" : "mt-16";

  const handleClickStep = (stepID: string) => {
    const step = getStepForStepID(blueprint, stepID) as BlueprintStep;
    if (!step) return;
    setSelectedStep(step);
    const cardElement = document.getElementById(`${step.id}-${step.template.name}`);
    if (cardElement) {
      cardElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  return (
    <div style={style} className={`fixed top-0 ${topClass} ml-5 h-fit z-50`}>
      <Alert
        size="sm"
        color="yellow"
        showWarningIcon
        dismissable
        onDismiss={() => setDismissed(true)}
        title={
          frontendStaleParameters.size === 1
            ? "1 step has stale parameters — update before publishing"
            : `${frontendStaleParameters.size} steps have stale parameters — update before publishing`
        }
        className="shadow-[0px_1.5px_4px_-1px_rgba(0,0,0,0.16)]"
      >
        <div className="flex flex-col space-y-1 w-full">
          <div className="flex flex-col space-y-0.5">
            {Array.from(frontendStaleParameters.entries()).map(([stepID, staleKeys]) => (
              <div key={stepID} className="flex items-baseline space-x-1.5">
                <button
                  className="text-xs font-medium underline cursor-pointer bg-transparent border-0 p-0 text-left"
                  onClick={() => handleClickStep(stepID)}
                >
                  {stepID}
                </button>
                <span className="text-xs text-yellow-800">{Array.from(staleKeys).join(", ")}</span>
              </div>
            ))}
          </div>
        </div>
      </Alert>
    </div>
  );
};

export default StaleParametersBanner;
